<script setup lang="ts">
import { orderChipsBySections } from "@carnet/ui/utils/filterChips";
import type { UrlFilterValues } from "@/composables/useUrlFilters";

const appliedModel = defineModel<UrlFilterValues>("applied", { default: () => ({}) });

defineProps<{
  searchChip?: { label: string };
}>();

const emit = defineEmits<{
  "clear:search": [];
}>();

const { t } = useI18n();
const data = await useGlobalData();

const SECTION_KEYS = [
  "zones",
  "environments",
  "localFeatures",
  "activityTypes",
  "siteDepthCategories",
  "siteConditions",
  "siteSituations",
  "mine",
] as const;
type SectionKey = (typeof SECTION_KEYS)[number];
type AccordionKey = Exclude<SectionKey, "mine">;

const { applied, apply, clearDraft, clearAppliedKey, syncDraftFromApplied, toggleDraft, isDraftSelected } = useUrlFilters(
  () => SECTION_KEYS as readonly string[]
);

watch(applied, (v) => (appliedModel.value = v), { immediate: true });

const sections = computed(() => [
  { key: "activityTypes" as const, title: t("sites.filters.type.title"), items: data.value?.activityTypes ?? [] },
  {
    key: "siteDepthCategories" as const,
    title: t("sites.filters.depth.title"),
    items: data.value?.siteDepthCategories ?? [],
  },
  {
    key: "siteSituations" as const,
    title: t("sites.filters.situation.title"),
    items: data.value?.siteSituations ?? [],
  },
  { key: "environments" as const, title: t("sites.filters.environment.title"), items: data.value?.environments ?? [] },
  {
    key: "siteConditions" as const,
    title: t("sites.filters.condition.title"),
    items: data.value?.siteConditions ?? [],
  },
  { key: "localFeatures" as const, title: t("sites.filters.localFeatures.title"), items: data.value?.localFeatures ?? [] },
]);

const accordionItems = computed(() =>
  sections.value.filter((s) => s.items.length > 0).map((s) => ({ value: s.key, label: s.title, slot: s.key as string }))
);

const open = ref(false);

watch(open, (isOpen) => {
  if (isOpen) syncDraftFromApplied();
});

function onApply() {
  apply();
  open.value = false;
}

function chipLabel(key: SectionKey, code: string): string {
  if (key === "mine") return t("sites.filters.mySites");
  const list = (data.value?.[key as AccordionKey] ?? []) as Array<{ code: string; label: string }>;
  return list.find((item) => item.code === code)?.label ?? code;
}

const chipEntries = computed(() =>
  orderChipsBySections(
    (Object.entries(applied.value) as [SectionKey, string[]][]).flatMap(([key, values]) =>
      values.map((value) => ({ key, value, label: chipLabel(key, value), removable: true }))
    ),
    sections.value.map((s) => s.key)
  )
);
</script>

<template>
  <WebFilters v-model:open="open" :empty="accordionItems.length === 0" @apply="onApply" @clear="clearDraft">
    <template #chips>
      <AppFilterChip v-if="searchChip" :label="searchChip.label" @remove="emit('clear:search')" />
      <AppFilterChips :chips="chipEntries" @remove="clearAppliedKey" />
    </template>

    <UAccordion :items="accordionItems" type="multiple">
      <template v-for="section in sections" :key="section.key" #[`${section.key}-body`]>
        <ul class="flex flex-col">
          <li v-for="item in section.items" :key="item.code">
            <label class="flex items-start gap-3 py-2 text-base cursor-pointer select-none">
              <input
                type="checkbox"
                class="accent-blue-700 size-4 rounded-xs my-px"
                :checked="isDraftSelected(section.key, item.code)"
                @change="toggleDraft(section.key, item.code)"
              />
              <span>{{ item.label }}</span>
            </label>
          </li>
        </ul>
      </template>
    </UAccordion>
  </WebFilters>
</template>
